import {GAME_TYPES} from '../../../constants/game';
import {TEXT_TURN_TIME_IS_UP} from '../../../constants/text/turn';
import {showToast} from '../../../redux/actions';
import {
  selectGameType,
  selectSharedGameState,
  selectTimerEnabled,
} from '../../../redux/reducers/sharedStateReducer/selectors';
import {selectTurnInputLockState} from '../../../redux/reducers/privateStateReducer/selectors';
import {checkIfActionTypeRelayable} from '../../../redux/relayedActions';
import {getActingPlayerFromState} from '../../players/getActingPlayer';
import {checkIfBotWorkScope} from '../../bots/online/botWorkScope';
import {TURN_AUTO_END_GRACE_MS, getPlayerOwnTurnDurationMs} from '../../phase/turnTimeLimits';
import {TURN_CONTEXT_FREE_ACTION_TYPES, createPhaseActionGuard} from './phaseActionGuard';

/**
 * БЛОКИРОВКА ВВОДА ПОСЛЕ НУЛЯ ТАЙМЕРА ХОДА (сетевая партия с таймером).
 *
 * Сервер закрывает ход по таймеру на отсрочку позже срока, который видит игрок
 * (TURN_AUTO_END_GRACE_MS): действие, сделанное до нуля, успевает доехать. Но
 * если игрок продолжает кликать и ПОСЛЕ нуля, его действие приезжает уже в
 * закрытый ход — сервер его отвергает и досылает полное состояние, клиент
 * показывает оверлей и перерисовывает поле. Поэтому с нуля своего времени и до
 * серверного закрытия клиент действий игрока не принимает: они не применяются
 * у него и не уходят на сервер. Вся отсрочка сервера остается на доставку
 * того, что уже отправлено.
 *
 * Устройство:
 *  - хук useTurnInputLock по таймеру ставит признак блокировки с ключом хода;
 *  - selectIsTurnInputLocked — действует ли признак сейчас: только пока ключ
 *    совпадает с текущим ходом и от игрока еще ждут действий. Смена хода,
 *    завершение его части этапа сервером, новая метка начала хода — и признак
 *    сам перестает действовать, снимать его не нужно;
 *  - страж turnInputGuardMiddleware отбрасывает игровые действия игрока, пока
 *    признак действует; входы с побочными эффектами (клик по полю, кнопка
 *    завершения хода, handleCompleteTurn) проверяют блокировку сами, до звуков
 *    и своих подсказок;
 *  - действия ботов, которые исполняет этот клиент, блокировка не трогает
 *    (utils/bots/online/botWorkScope).
 *
 * Часы клиента от серверных только отстают (серверное время оценивается в
 * момент обработки сообщения), так что клиентский ноль не раньше серверного
 * срока и времени у игрока блокировка не отнимает. На случай, когда закрытие
 * от сервера не приходит (сбитые часы устройства, зависший канал), признак
 * снимает предохранитель — TURN_INPUT_LOCK_MAX_MS.
 */

// Сколько самое большее держится блокировка. Закрытие от сервера приходит
// через отсрочку плюс доставка; не пришло за это время — часы клиента и сервера
// разошлись, и держать игрока дальше хуже, чем вернуть прежнее поведение
export const TURN_INPUT_LOCK_MAX_MS = TURN_AUTO_END_GRACE_MS + 3000;

/**
 * Ключ хода для блокировки: контекст хода (раунд, фаза, этап, ходящий),
 * действующий игрок и метка начала отрезка. Метка в ключе — чтобы блокировка,
 * поставленная по одной метке, не действовала после прихода новой
 */
export const getTurnInputLockKey = (gameState, actingPlayerId) => {
  const {round, phase, warPhaseStage, currentPlayerId} = createPhaseActionGuard(gameState);
  return [round, phase, warPhaseStage || '', currentPlayerId || '', actingPlayerId, gameState.turnEndTimestamp].join('|');
};

/**
 * Что блокировать: ключ хода и серверное время, когда у действующего игрока
 * выходит срок, — или null, если блокировать нечего (не сетевая партия, таймер
 * выключен, действовать сейчас не ему, метки начала хода нет).
 *
 * @returns {{turnKey: string, deadlineMs: number}|null}
 */
export const selectTurnInputLockTarget = (state) => {
  if (selectGameType(state) !== GAME_TYPES.ONLINE || !selectTimerEnabled(state)) {
    return null;
  }
  const gameState = selectSharedGameState(state);
  if (!gameState?.turnEndTimestamp) {
    return null;
  }
  const actingPlayerId = getActingPlayerFromState(state)?.id;
  const ownDurationMs = getPlayerOwnTurnDurationMs(gameState, actingPlayerId);
  if (ownDurationMs === null) {
    return null;
  }
  return {
    turnKey: getTurnInputLockKey(gameState, actingPlayerId),
    deadlineMs: gameState.turnEndTimestamp + ownDurationMs,
  };
};

// Примитивы для хука: объект цели на каждый вызов новый
export const selectTurnInputLockKey = (state) => selectTurnInputLockTarget(state)?.turnKey ?? null;
export const selectTurnInputLockDeadlineMs = (state) => selectTurnInputLockTarget(state)?.deadlineMs ?? null;

/** Действует ли блокировка ввода сейчас */
export const selectIsTurnInputLocked = (state) => {
  const {lockedTurnKey} = selectTurnInputLockState(state);
  return Boolean(lockedTurnKey) && selectTurnInputLockKey(state) === lockedTurnKey;
};

/**
 * Заблокировано ли действие, которое выполняется прямо сейчас: блокировка
 * действует и это не работа бота. Для входов с побочными эффектами — проверять
 * первой строкой, до звуков, подсказок и приватных изменений
 */
export const checkIfTurnInputBlocked = (state) => !checkIfBotWorkScope() && selectIsTurnInputLocked(state);

// Экшены, которые уходят на сервер, но к ходу игрока не привязаны: чат (его
// пишут в любой момент), жетоны плана Элементалей (чужой ход) и служебные
const LOCK_EXEMPT_ACTION_TYPES = new Set([
  ...TURN_CONTEXT_FREE_ACTION_TYPES,
  'ELEMENTALS_SET_PLAN_TOKENS',
  'SET_GAME_TYPE',
  'START_GAME',
  'SET_PLAYERS',
]);

// Подсказка — не чаще этого: клики по полю сыплются часто, а тост в приложении
// один (новый затирает старый)
const BLOCKED_TOAST_THROTTLE_MS = 1500;
const BLOCKED_TOAST_DURATION_MS = 2000;
let lastBlockedToastAt = 0;
let lastReportedTurnKey = null;
let blockedAttemptListener = null;

/**
 * Слушатель «игрок попытался действовать под блокировкой» — раз на ход (первая
 * попытка). Клиент ставит сюда событие аналитики (см. redux/store): модуль
 * читает и сервер, которому аналитика браузера ни к чему
 */
export const setTurnInputBlockedListener = (listener) => {
  blockedAttemptListener = listener;
};

/**
 * Игрок попытался действовать под блокировкой: подсказка «Время хода вышло» (с
 * глушением повторов) и, на первую попытку за ход, слушатель. Зовет каждый
 * вход, который отказал игроку: страж, клик по полю, кнопка завершения хода
 */
export const notifyTurnInputBlocked = (dispatch, state, nowMs = Date.now()) => {
  const {lockedTurnKey} = selectTurnInputLockState(state);
  if (lockedTurnKey !== lastReportedTurnKey) {
    lastReportedTurnKey = lockedTurnKey;
    blockedAttemptListener?.();
  }
  if (nowMs - lastBlockedToastAt < BLOCKED_TOAST_THROTTLE_MS) {
    return;
  }
  lastBlockedToastAt = nowMs;
  // Без noHideOnTurnEnd: смена ходящего игрока подсказку уберет (Toast), а конец
  // блокировки при том же ходящем (следующий этап войны) — useTurnInputLock
  dispatch(showToast(TEXT_TURN_TIME_IS_UP, 'warning', BLOCKED_TOAST_DURATION_MS, true));
};

/**
 * Игровое действие хода, совершенное на этом клиенте: то, что ушло бы на
 * сервер со слепком хода (см. networkMiddleware) или закрыло бы ход. Пришедшее
 * с сервера и локальные экшены интерфейса сюда не входят
 */
export const checkIfLocalTurnAction = (action) => Boolean(action)
  && typeof action.type === 'string'
  && !action._fromServer
  && !action.noServerSync
  && checkIfActionTypeRelayable(action.type)
  && !LOCK_EXEMPT_ACTION_TYPES.has(action.type);
